import { useEffect, useState } from "react";
import { getAttempt } from "../services/ProgressService";
import { EmptyState } from "./ui";
import { CheckCircle, CrossCircle, DashCircle, Quiz } from "./icons";

/**
 * One attempt, question by question — the learner's answer sheet.
 *
 * The review table says "18 / 24" and stops there, which is a record but not a
 * lesson: the thing you want after a test is to see WHICH answers went wrong
 * and what the right one was. This is that view.
 *
 * Everything shown comes from the server's verdict for that submission — the
 * marks the grader awarded, per question. Nothing is re-graded here, so the
 * sheet can never contradict the score printed above it.
 */

/**
 * The four verdicts a question can carry, in the design system's own semantic
 * colours rather than hand-picked hexes — so a palette change reaches here too.
 * "Partly correct" borrows the warn ramp: it is neither a pass nor a failure.
 */
const STATUS = {
  correct: {
    label: "Correct",
    icon: CheckCircle,
    bg: "var(--success-bg)",
    fg: "var(--success-ink)",
    edge: "var(--success)",
  },
  partial: {
    label: "Partly correct",
    icon: DashCircle,
    bg: "var(--warn-bg)",
    fg: "var(--warn-ink)",
    edge: "var(--warn)",
  },
  incorrect: {
    label: "Incorrect",
    icon: CrossCircle,
    bg: "var(--danger-bg)",
    fg: "var(--danger-ink)",
    edge: "var(--danger)",
  },
  unanswered: {
    label: "Not answered",
    icon: DashCircle,
    bg: "var(--surface-hover)",
    fg: "var(--ink-2)",
    edge: "var(--border-strong)",
  },
};

/** Marks read as "3" and "2.5", never "3.00" — trailing zeros are noise. */
const fmtMark = (n) => {
  const value = Number(n) || 0;
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100);
};

const TYPE_LABEL = {
  multipleChoice: "Multiple choice",
  singleChoice: "Single choice",
  yesNo: "Yes / No",
  descriptive: "Descriptive",
  matchFollowing: "Match the following",
  fillBlank: "Fill in the blanks",
  ordering: "Ordering",
};

const Tally = ({ tone, count, label }) => (
  <div
    style={{
      background: tone.bg,
      color: tone.fg,
      borderRadius: 10,
      padding: "8px 12px",
      minWidth: 92,
    }}
  >
    <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
      {count}
    </div>
    <div style={{ fontSize: 11.5, fontWeight: 600 }}>{label}</div>
  </div>
);

/** A titled block of answer lines — "Your answer" / "Correct answer". */
const AnswerBlock = ({ title, lines, tone, empty }) => (
  <div style={{ minWidth: 0, flex: "1 1 220px" }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "var(--ink-3)",
        marginBottom: 4,
      }}
    >
      {title}
    </div>
    {lines.length === 0 ? (
      <div style={{ fontSize: 13.5, color: "var(--ink-3)", fontStyle: "italic" }}>
        {empty}
      </div>
    ) : (
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
        {lines.map((line, i) => (
          <li
            key={`${line}-${i}`}
            style={{
              fontSize: 13.5,
              lineHeight: 1.5,
              color: tone,
              // Long descriptive answers must wrap rather than stretch the card.
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {line}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/**
 * The per-part table for match / blanks / ordering.
 *
 * These types are the whole reason an answer sheet beats a score: a learner who
 * got three of five pairs right needs to know WHICH two, and a flat "Incorrect"
 * on the question as a whole tells them nothing.
 */
const PartsTable = ({ parts }) => (
  <div style={{ overflowX: "auto", marginTop: 10 }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 320 }}>
      <thead>
        <tr style={{ textAlign: "left", color: "var(--ink-3)" }}>
          <th style={{ padding: "5px 8px 5px 0", fontWeight: 600 }}>Item</th>
          <th style={{ padding: "5px 8px", fontWeight: 600 }}>Your answer</th>
          <th style={{ padding: "5px 0 5px 8px", fontWeight: 600 }}>Correct answer</th>
        </tr>
      </thead>
      <tbody>
        {parts.map((part, i) => (
          <tr key={`${part.label}-${i}`} style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <td style={{ padding: "7px 8px 7px 0", fontWeight: 600 }}>{part.label}</td>
            <td
              style={{
                padding: "7px 8px",
                color: part.correct ? "var(--success-ink)" : "var(--danger-ink)",
                overflowWrap: "anywhere",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                {part.correct ? <CheckCircle size={14} /> : <CrossCircle size={14} />}
                {part.yours || <em style={{ opacity: 0.7 }}>blank</em>}
              </span>
            </td>
            <td style={{ padding: "7px 0 7px 8px", overflowWrap: "anywhere" }}>
              {part.expected}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const QuestionRow = ({ q }) => {
  const tone = STATUS[q.status] || STATUS.incorrect;
  const Icon = tone.icon;
  // A per-part table already shows both sides item by item; repeating them as
  // flat lists above it says the same thing twice.
  const showBlocks = q.parts.length === 0;

  return (
    <div
      style={{
        borderLeft: `3px solid ${tone.edge}`,
        background: "var(--surface-sunken)",
        borderRadius: "0 10px 10px 0",
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <span style={{ color: tone.fg, marginTop: 2 }}>
          <Icon size={18} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, overflowWrap: "anywhere" }}>
            {q.no}. {q.text}
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--ink-3)",
              marginTop: 3,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{TYPE_LABEL[q.type] || q.type}</span>
            <span>·</span>
            <span>{tone.label}</span>
            {q.notRecorded && (
              <>
                <span>·</span>
                <span>Answer not kept on record</span>
              </>
            )}
            {q.autoScored && (
              <>
                <span>·</span>
                <span>Scored automatically, not yet reviewed by a trainer</span>
              </>
            )}
          </div>
        </div>
        <span
          style={{
            flexShrink: 0,
            background: tone.bg,
            color: tone.fg,
            fontWeight: 700,
            fontSize: 12,
            padding: "3px 10px",
            borderRadius: 20,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtMark(q.awarded)} / {fmtMark(q.outOf)}
        </span>
      </div>

      <div style={{ paddingLeft: 28, marginTop: 8 }}>
        {showBlocks && (
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <AnswerBlock
              title="Your answer"
              lines={q.yourAnswer}
              tone={q.status === "correct" ? "var(--success-ink)" : "var(--danger-ink)"}
              // Two different facts, and conflating them is what made a
              // full-marks question read as skipped: the grader marked this
              // one, but older submissions did not store what was actually
              // put down.
              empty={
                q.notRecorded
                  ? "This attempt did not store your answer — only the marks it earned."
                  : "You left this blank."
              }
            />
            <AnswerBlock
              title={q.type === "descriptive" ? "Model answer" : "Correct answer"}
              lines={q.correctAnswer}
              tone="var(--ink)"
              empty="Not recorded."
            />
          </div>
        )}

        {q.parts.length > 0 && <PartsTable parts={q.parts} />}

        {q.reviewFeedback && (
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 13,
              background: "var(--brand-50)",
              color: "var(--brand-700)",
              padding: "8px 10px",
              borderRadius: 8,
            }}
          >
            <strong>Trainer's note:</strong> {q.reviewFeedback}
          </p>
        )}

        {q.feedback && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 13,
              color: "var(--ink-3)",
            }}
          >
            <strong>Why:</strong> {q.feedback}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Fetches and renders one attempt. `attemptId` is the submission's own id
 * rather than its position, so the sheet cannot drift onto a neighbouring
 * attempt if the list is ever re-counted.
 */
export const AnswerSheet = ({ attemptId }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    if (!attemptId) return;
    let live = true;
    setState("loading");
    (async () => {
      try {
        const res = await getAttempt(attemptId);
        if (!live) return;
        setData(res);
        setState(res ? "ready" : "error");
      } catch {
        if (live) setState("error");
      }
    })();
    return () => {
      live = false;
    };
  }, [attemptId]);

  if (state === "loading") {
    return (
      <p className="ds-sub" style={{ padding: "10px 0", margin: 0 }}>
        Loading your answers…
      </p>
    );
  }

  if (state === "error" || !data) {
    return (
      <EmptyState
        icon={Quiz}
        text="This attempt's answers could not be loaded. The assessment may have been removed since you took it."
      />
    );
  }

  const s = data.summary || {};

  return (
    <div className="ds-stack ds-gap-3" style={{ paddingTop: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Tally tone={STATUS.correct} count={s.correct || 0} label="Correct" />
        {s.partial > 0 && (
          <Tally tone={STATUS.partial} count={s.partial} label="Partly right" />
        )}
        <Tally tone={STATUS.incorrect} count={s.incorrect || 0} label="Incorrect" />
        {s.unanswered > 0 && (
          <Tally tone={STATUS.unanswered} count={s.unanswered} label="Skipped" />
        )}
      </div>

      {data.questions.length === 0 ? (
        <EmptyState
          icon={Quiz}
          text="This paper has no questions on it any more, so there is nothing to show."
        />
      ) : (
        <div className="ds-stack ds-gap-2">
          {data.questions.map((q) => (
            <QuestionRow key={q.questionId} q={q} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnswerSheet;
